import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useStore } from '../../store/useStore';
import toast from 'react-hot-toast';

interface AddPropertyFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function AddPropertyForm({ onClose, onSuccess }: AddPropertyFormProps) {
  const { currentUser } = useStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    address: '',
    city: '',
    state: '',
    zip: '',
    property_type: 'single-family',
    beds: '',
    baths: '',
    sqft: '',
    year_built: '',
    status: 'vacant'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    setIsSubmitting(true);

    try {
      const { error } = await supabase.from('properties').insert({
        team_id: currentUser.teamId,
        owner_id: currentUser.id,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        property_type: formData.property_type,
        beds: formData.beds ? parseInt(formData.beds) : null,
        baths: formData.baths ? parseFloat(formData.baths) : null,
        sqft: formData.sqft ? parseInt(formData.sqft) : null,
        year_built: formData.year_built ? parseInt(formData.year_built) : null,
        status: formData.status
      });

      if (error) throw error;
      toast.success('Property added successfully');
      onSuccess();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6 p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Street Address</label>
          <input required type="text" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2 text-white focus:border-[var(--t-primary)] outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">City</label>
          <input required type="text" value={formData.city} onChange={e => setFormData({ ...formData, city: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2 text-white focus:border-[var(--t-primary)] outline-none" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">State</label>
            <input required type="text" value={formData.state} onChange={e => setFormData({ ...formData, state: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2 text-white focus:border-[var(--t-primary)] outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Zip Code</label>
            <input required type="text" value={formData.zip} onChange={e => setFormData({ ...formData, zip: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2 text-white focus:border-[var(--t-primary)] outline-none" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Property Type</label>
          <select value={formData.property_type} onChange={e => setFormData({ ...formData, property_type: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2.5 text-white focus:border-[var(--t-primary)] outline-none">
            <option value="single-family">Single Family</option>
            <option value="multi-family">Multi Family</option>
            <option value="condo">Condo / Townhouse</option>
            <option value="commercial">Commercial</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Status</label>
          <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2.5 text-white focus:border-[var(--t-primary)] outline-none">
            <option value="vacant">Vacant</option>
            <option value="occupied">Occupied</option>
            <option value="listed-for-rent">Listed For Rent</option>
            <option value="under-maintenance">Under Maintenance</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Beds</label>
          <input type="number" value={formData.beds} onChange={e => setFormData({ ...formData, beds: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2 text-white focus:border-[var(--t-primary)] outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Baths</label>
          <input type="number" step="0.5" value={formData.baths} onChange={e => setFormData({ ...formData, baths: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2 text-white focus:border-[var(--t-primary)] outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Square Feet</label>
          <input type="number" value={formData.sqft} onChange={e => setFormData({ ...formData, sqft: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2 text-white focus:border-[var(--t-primary)] outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Year Built</label>
          <input type="number" value={formData.year_built} onChange={e => setFormData({ ...formData, year_built: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2 text-white focus:border-[var(--t-primary)] outline-none" />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-[var(--t-border)]">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-[var(--t-text-muted)] hover:text-white transition-colors">Cancel</button>
        <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-[var(--t-primary)] text-white text-sm font-bold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors disabled:opacity-50">
          {isSubmitting ? 'Saving...' : 'Add Property'}
        </button>
      </div>
    </form>
  );
}
